import type { PostTypeFields } from '@tribeplatform/gql-client/graphql'
import type {
  PostType,
  MutationAddPostTypeFieldArgs,
  Network,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'
import { getPostTypesKey, getPostTypeKey } from '../../utils/keys/post.key.js'

export const useAddPostTypeField = (options?: {
  fields?: PostTypeFields
  useMutationOptions?: UseMutationOptions<
    PostType,
    Error,
    MutationAddPostTypeFieldArgs
  >
}) => {
  const { useMutationOptions, fields = 'basic' } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const postTypesKey = getPostTypesKey()
  const networkKey = getNetworkKey()

  return useMutation<PostType, Error, MutationAddPostTypeFieldArgs>(
    input => client.postType.addField(input, fields),
    {
      onMutate: variables => {
        const postTypeKey = getPostTypeKey({ variables: { id: variables?.id } })

        queryClient.cancelQueries(postTypesKey)
        queryClient.cancelQueries(postTypeKey)

        queryClient.setQueriesData<PostType>(postTypeKey, old => ({
          ...old,
          postFields: {
            fields: [...old?.postFields?.fields, variables?.input],
          },
        }))

        queryClient.setQueriesData<Network>(networkKey, old => ({
          ...old,
          postTypes: old?.postTypes?.map(postType => ({
            ...postType,
            postFields: {
              fields: [
                ...(postType?.postFields?.fields || []),
                ...(postType?.id === variables?.id ? [variables?.input] : []),
              ],
            },
          })),
        }))
      },
      onSettled: variables => {
        const postTypeKey = getPostTypeKey({ variables: { id: variables?.id } })

        queryClient.invalidateQueries(postTypesKey)
        queryClient.invalidateQueries(postTypeKey)
        queryClient.invalidateQueries(networkKey)
      },
      ...useMutationOptions,
    },
  )
}
